import { useCallback, useMemo, useRef, useEffect } from 'react';
import { type Status } from '@elevenlabs/client';
import { useWidgetStore } from '../store';
import ConversationService from '../services/ConversationService';
import { ProcessedConfig } from '../services/ConfigurationService';
import { Message, ConversationItem } from '../store/types';
import { translateRuntimeError } from '../utils/errorMessages';
import { getStrings } from '../i18n/strings';
import {
  captureWidgetEvent,
  errorWidgetTelemetryProperties,
} from '../services/TelemetryService';
import {
  type ElevenLabsConversationAdapter,
  type ElevenLabsStartOptions,
  useElevenLabsConversation,
} from './useElevenLabsConversation';

interface UseConversationConnectionReturn {
  conversation: ElevenLabsConversationAdapter;
  conversationService: ConversationService;
  conversationId: string | null;
  isConnected: boolean;
  startConversation: (options: ElevenLabsStartOptions) => Promise<string>;
  stopConversation: (options?: { preserveConversation?: boolean }) => Promise<void>;
  markUserActivity: () => void;
}

function normalizeMessageText(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

export const useConversationConnection = (
  config: ProcessedConfig,
  widgetId: string,
  isWidgetActive: boolean,
  onIdleTimeout?: () => void | Promise<void>,
  onRemoteDisconnect?: () => void | Promise<void>
): UseConversationConnectionReturn => {
  const conversationId = useWidgetStore((state) => state.conversationId);

  const conversationIdRef = useRef(conversationId);
  const isManualOverrideRef = useRef(false); // Флаг для предотвращения race condition с useEffect
  const preserveConversationOnDisconnectRef = useRef(false);
  const onIdleTimeoutRef = useRef(onIdleTimeout);
  const onRemoteDisconnectRef = useRef(onRemoteDisconnect);
  const lastUserActivitySignalAtRef = useRef<number | null>(null);

  // Idle-timeout: when neither party has produced meaningful activity for
  // config.idleTimeoutMs, end the session gracefully. Re-armed if the timer
  // fires while the agent is mid-utterance so we never cut the agent off.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectSafetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimeoutMs = config.idleTimeoutMs ?? 60_000;

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    onIdleTimeoutRef.current = onIdleTimeout;
  }, [onIdleTimeout]);

  useEffect(() => {
    onRemoteDisconnectRef.current = onRemoteDisconnect;
  }, [onRemoteDisconnect]);

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const clearDisconnectSafetyTimer = () => {
    if (disconnectSafetyTimerRef.current) {
      clearTimeout(disconnectSafetyTimerRef.current);
      disconnectSafetyTimerRef.current = null;
    }
  };

  const armIdleTimer = () => {
    clearIdleTimer();
    if (!idleTimeoutMs || idleTimeoutMs <= 0) return;
    idleTimerRef.current = setTimeout(() => {
      const speaking = useWidgetStore.getState().isAgentSpeaking;
      if (speaking) {
        // Don't cut the agent off — re-arm and check again next cycle.
        armIdleTimer();
        return;
      }
      console.log(`[idle-timer] ${idleTimeoutMs}ms of silence, ending session`);
      const stoppedConversationId = conversationIdRef.current;

      isManualOverrideRef.current = true;
      preserveConversationOnDisconnectRef.current = true;
      addIdleEndedNotice();
      useWidgetStore.getState().setIsAgentSpeaking(false);
      const endSdkSession = (conversationServiceRef.current?.endSession() ?? Promise.resolve())
        .catch((err) => {
          console.warn('[idle-timer] endSession failed:', err);
        })
        .finally(() => {
          if (stoppedConversationId === null || conversationIdRef.current === stoppedConversationId) {
            cleanupDisconnectedState();
          }
        });

      const idleCleanup = onIdleTimeoutRef.current;
      if (idleCleanup) {
        void endSdkSession.then(() => idleCleanup()).catch((err) => {
          console.warn('[idle-timer] idle cleanup failed:', err);
        });
        return;
      }

      void endSdkSession;
    }, idleTimeoutMs);
  };

  const addIdleEndedNotice = () => {
    addSessionNotice(getStrings(config.language).idleEndedMessage, 'idle-timeout');
  };

  const addRemoteEndedNotice = () => {
    addSessionNotice(getStrings(config.language).sessionEndedMessage, 'remote-disconnect');
  };

  const addSessionNotice = (text: string, idPrefix: string) => {
    const duplicateIdleMessage = useWidgetStore
      .getState()
      .conversationItems.some((item) => (
        item.type === 'message' &&
        item.message?.author === 'agent' &&
        item.message.text === text
      ));

    if (duplicateIdleMessage) return;

    const timestamp = Date.now();
    const idleMessage: Message = {
      text,
      author: 'agent',
      id: `${idPrefix}-${timestamp}`,
    };
    const conversationItem: ConversationItem = {
      type: 'message',
      id: `${idPrefix}-item-${timestamp}`,
      timestamp,
      message: idleMessage,
    };

    useWidgetStore.getState().addMessage(idleMessage);
    useWidgetStore.getState().addConversationItem(conversationItem);
  };

  // Forward-declared so callbacks below can reach the service before its
  // useMemo materialises on the next render.
  const conversationServiceRef = useRef<ConversationService | null>(null);

  const cleanupDisconnectedState = () => {
    clearIdleTimer();
    clearDisconnectSafetyTimer();
    isManualOverrideRef.current = false;
    conversationIdRef.current = null;
    lastUserActivitySignalAtRef.current = null;

    useWidgetStore.getState().setConversationId(null);
    useWidgetStore.getState().setButtonState('idle');
    useWidgetStore.getState().setIsRecording(false);
    useWidgetStore.getState().setIsAgentSpeaking(false);
    useWidgetStore.getState().setIsAgentThinking(false);

    if (!preserveConversationOnDisconnectRef.current) {
      useWidgetStore.getState().clearMessages();
      useWidgetStore.getState().clearTranscript();
      useWidgetStore.getState().clearConversationItems();
    }

    preserveConversationOnDisconnectRef.current = false;
  };

  // ElevenLabs conversation hook
  const conversation = useElevenLabsConversation({
    onConnect: () => {
      console.log('[Conversation] Connected');
      captureWidgetEvent('widget_conversation_connected', {
        widget_id: widgetId,
        mode: config.mode,
      });
      armIdleTimer();
    },
    onMessage: ({ message, source }: { message: string; source: string }) => {
      // Re-arm on agent messages and on meaningful user transcripts. Empty or
      // tiny VAD artifacts should not keep an abandoned voice session alive.
      if (source === 'ai') {
        armIdleTimer();
        // Clear thinking indicator on first agent response
        useWidgetStore.getState().setIsAgentThinking(false);
        const normalizedMessage = normalizeMessageText(message);
        const duplicateAgentMessage = useWidgetStore
          .getState()
          .conversationItems.some((item) => (
            item.type === 'message' &&
            item.message?.author === 'agent' &&
            normalizeMessageText(item.message.text) === normalizedMessage
          ));

        if (duplicateAgentMessage) {
          return;
        }

        const agentMessage: Message = {
          text: message,
          author: 'agent',
          id: Date.now().toString(),
        };
        const conversationItem: ConversationItem = {
          type: 'message',
          id: Date.now().toString(),
          timestamp: Date.now(),
          message: agentMessage,
        };
        useWidgetStore.getState().addMessage(agentMessage);
        useWidgetStore.getState().addConversationItem(conversationItem);
      } else if (source === 'user' && normalizeMessageText(message).length >= 3) {
        armIdleTimer();
      }
    },
    onStatusChange: ({ status }: { status: Status }) => {
      console.log('[Conversation] Status changed:', status);

      // Синхронизируем наш store с реальным статусом SDK
      useWidgetStore.getState().setConnectionStatus(
        status === 'connected' ? 'connected' :
        status === 'disconnected' ? 'disconnected' :
        'connecting'
      );

      // Когда реально отключились - чистим UI
      if (status === 'disconnected') {
        if (conversationIdRef.current !== null) {
          if (!isManualOverrideRef.current) {
            preserveConversationOnDisconnectRef.current = true;
            addRemoteEndedNotice();

            const remoteDisconnectCleanup = onRemoteDisconnectRef.current;
            if (remoteDisconnectCleanup) {
              void Promise.resolve(remoteDisconnectCleanup()).catch((err) => {
                console.warn('[Conversation] remote disconnect cleanup failed:', err);
              });
            }
          }
          cleanupDisconnectedState();
        }
      }
    },
    onDisconnect: async () => {
      console.log('[Conversation] Disconnected');
      // onStatusChange уже делает cleanup, здесь только логирование
    },
    onError: (error) => {
      const errorMessage = typeof error === 'string' ? error : 'Unknown error';
      console.error('[Conversation] Error:', errorMessage);
      captureWidgetEvent('widget_conversation_error', {
        widget_id: widgetId,
        ...errorWidgetTelemetryProperties(error),
      });
      useWidgetStore.getState().setError(translateRuntimeError(errorMessage));
    },
  });

  const markUserActivity = useCallback(() => {
    if (conversationIdRef.current === null) return;
    armIdleTimer();

    const now = Date.now();
    if (
      lastUserActivitySignalAtRef.current !== null &&
      now - lastUserActivitySignalAtRef.current < 1000
    ) {
      return;
    }
    lastUserActivitySignalAtRef.current = now;

    if (typeof conversation.sendUserActivity === 'function') {
      try {
        conversation.sendUserActivity();
      } catch (error) {
        console.warn('[Conversation] sendUserActivity failed:', error);
      }
    }
  }, [conversation, idleTimeoutMs]);

  // Синхронизируем conversation.isSpeaking со store
  // Это позволяет UI получать актуальное значение из store, которое мы можем контролировать
  // НО не синхронизируем во время manual override (остановка разговора)
  useEffect(() => {
    if (!isManualOverrideRef.current) {
      useWidgetStore.getState().setIsAgentSpeaking(conversation.isSpeaking);
    }
  }, [conversation.isSpeaking]);

  const conversationService = useMemo(
    () => ConversationService.create(conversation),
    [conversation]
  );

  // Mirror the service into a ref so timer callbacks (set up at render-1)
  // can reach the latest instance without retriggering on every change.
  useEffect(() => {
    conversationServiceRef.current = conversationService;
  }, [conversationService]);

  // Always clear pending timers on unmount.
  useEffect(() => {
    return () => {
      clearIdleTimer();
      clearDisconnectSafetyTimer();
    };
  }, []);

  const startConversation = useCallback(
    async (options: ElevenLabsStartOptions) => {
      const startedAt = Date.now();
      captureWidgetEvent('widget_conversation_start_requested', {
        widget_id: widgetId,
        text_only: options.textOnly,
        connection_type: options.connectionType || 'websocket',
      });

      try {
        const sessionId = await conversationService.startSession(options);
        conversationIdRef.current = sessionId;
        lastUserActivitySignalAtRef.current = null;
        useWidgetStore.getState().setConversationId(sessionId);
        useWidgetStore.getState().setButtonState(options.textOnly ? 'idle' : 'recording');
        useWidgetStore.getState().setIsRecording(!options.textOnly);
        captureWidgetEvent('widget_conversation_start_succeeded', {
          widget_id: widgetId,
          text_only: options.textOnly,
          duration_ms: Date.now() - startedAt,
        });
        return sessionId;
      } catch (error) {
        captureWidgetEvent('widget_conversation_start_failed', {
          widget_id: widgetId,
          text_only: options.textOnly,
          duration_ms: Date.now() - startedAt,
          ...errorWidgetTelemetryProperties(error),
        });
        throw error;
      }
    },
    [conversationService, widgetId]
  );

  const stopConversation = useCallback(async (options?: { preserveConversation?: boolean }) => {
    console.log('[stopConversation] Stopping, current status:', conversation.status);
    preserveConversationOnDisconnectRef.current = options?.preserveConversation === true;
    const stoppedConversationId = conversationIdRef.current;

    // Немедленно сбрасываем UI индикатор speaking
    // Устанавливаем флаг manual override чтобы useEffect не перезаписал значение обратно
    isManualOverrideRef.current = true;
    useWidgetStore.getState().setIsAgentSpeaking(false);

    try {
      // Вызываем endSession - это останавливает аудио и закрывает WebSocket немедленно
      // ConversationService теперь всегда передаёт команду в SDK без блокировки
      await conversationService.endSession();
      console.log('[stopConversation] endSession completed successfully');
    } catch (error) {
      console.error('[stopConversation] endSession failed:', error);
      throw error;
    }

    if (stoppedConversationId === null || conversationIdRef.current === stoppedConversationId) {
      cleanupDisconnectedState();
    }

    // Safety timeout: ВСЕГДА устанавливаем независимо от успеха/ошибки endSession
    // Это гарантирует, что UI сбросится даже если SDK не вызовет onStatusChange
    console.warn('[stopConversation] Setting safety timeout (1s)');
    clearDisconnectSafetyTimer();
    disconnectSafetyTimerRef.current = setTimeout(() => {
      disconnectSafetyTimerRef.current = null;

      // Проверяем: произошёл ли cleanup через onStatusChange?
      // Если conversationId ещё не null, значит onStatusChange не сработал
      if (conversationIdRef.current !== null && conversationIdRef.current === stoppedConversationId) {
        console.error('[stopConversation] FORCE CLEANUP - UI still active after 1s');
        cleanupDisconnectedState();
      } else {
        console.log('[stopConversation] Cleanup already done via onStatusChange, no action needed');
      }
    }, 1000);
  }, [conversationService, conversation]);

  return {
    conversation,
    conversationService,
    conversationId,
    isConnected: conversation.status === 'connected',
    startConversation,
    stopConversation,
    markUserActivity,
  };
};
